/**
 * Server-side Logger for Next.js
 *
 * A utility that wraps the native console object to only log on the server side.
 * Provides all the same methods as the native console object.
 */

// Check if we're on the server (window is undefined)
const isServer = typeof window === "undefined"

// Create a proxy handler for console methods
const handler: ProxyHandler<Console> = {
  get: (target, prop: string | symbol) => {
    // If we're on the server, return the actual console method
    if (isServer && typeof prop === "string") {
      const method = console[prop as keyof Console]
      if (typeof method === "function") {
        return (...args: any[]) => {
          return (console[prop as keyof Console] as Function)(...args)
        }
      }
    }

    // Return a no-op function if we're on the client
    // or if the requested method doesn't exist
    return () => {}
  },
}

// Create the proxy with the console object as target
const serverLogger = new Proxy(console, handler)

export default serverLogger

/**
 * Usage examples:
 *
 * import serverLogger from '@/utils/server-logger';
 *
 * // These will only log on the server
 * serverLogger.log('Server message');
 * serverLogger.info('Server info');
 * serverLogger.warn('Server warning');
 * serverLogger.error('Server error');
 * serverLogger.debug('Server debug');
 * serverLogger.trace('Stack trace');
 * serverLogger.table([{ id: 1, name: 'Test' }]);
 * serverLogger.group('Group');
 * serverLogger.groupEnd();
 *
 * // You can also use it with more complex arguments
 * serverLogger.log('User data:', { id: 123, name: 'John' });
 */
